import { amplitudeTrack, clearAnalyticsUser, setAnalyticsUser, setAnalyticsUserProperties } from '@cw-elements/analytics-provider';
import type { AdditionalAnalyticsInfo, SegmentTrackingHookReturn } from '@protector/utils/types';
import { getCurrentWorkspace, getLicensingStatus } from 'core/core.selectors';
import { getSelectedCompany } from 'entities/company/company.selectors';
import { getSelectedProperty } from 'entities/property/property.selectors';
import { getPlanInfo } from 'infra/events/events.utils';
import { captureError } from 'infra/monitoring/monitoring.utils';
import { useGetWorkspace } from 'querys/workspace/workspace.query';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import type { AnalyticsInfo } from './user-events.types';

export type { SegmentTrackingHookReturn } from '@protector/utils/types';

const ASSET = 'Web Panel';

const handleError = (condition: boolean, message: string) => {
  if (condition) {
    captureError(new Error(message), { errorType: 'ValidationError', domain: 'Segment' });
  }
};

export const handleResetSegment = () => {
  clearAnalyticsUser();
};

const useSegmentTracking = <C extends boolean = true, P extends boolean = true, W extends boolean = true>(
  includeCompany: C = true as C,
  includeProperty: P = true as P,
  includeWorkspace: W = true as W
): SegmentTrackingHookReturn<C, P> => {
  const company = useSelector(getSelectedCompany);
  const property = useSelector(getSelectedProperty);
  const workspace = useSelector(getCurrentWorkspace);
  const licensingStatus = useSelector(getLicensingStatus);
  const { data: workspaceResponse } = useGetWorkspace(includeWorkspace ? workspace?.id : undefined);

  const workspacePayload = useMemo(() => {
    if (!includeWorkspace || !workspaceResponse) return {};
    const workspaceInfo = workspaceResponse.data;
    return { workspaceId: workspaceInfo.id, workspaceName: workspaceInfo.name, workspaceType: workspaceInfo.use_type };
  }, [workspaceResponse, includeWorkspace]);
  const companyPayload = useMemo(() => {
    if (!includeCompany || !company) return {};

    return { organizationId: company.id, organizationName: company.name };
  }, [company, includeCompany]);

  const planPayload = useMemo(
    () => ({ planId: getPlanInfo(licensingStatus, 'id'), planName: getPlanInfo(licensingStatus, 'name'), asset: ASSET }),
    [licensingStatus]
  );

  const propertyPayload = useMemo(() => {
    if (!includeProperty || !property) return {};

    return { farmId: property.id, farmName: property.name };
  }, [property, includeProperty]);

  const track = useCallback(
    (action: string, additionalInfo: AdditionalAnalyticsInfo<C, P> = {}) => {
      handleError(!company && includeCompany, `Company is not present when tracking action ${action}.`);
      handleError(!property && includeProperty, `Property is not present when tracking action ${action}.`);
      handleError(!workspace && includeWorkspace, `Workspace is not present when tracking action ${action}.`);

      handleError(
        includeCompany && !!(additionalInfo.organizationId ?? additionalInfo.organizationName),
        `The event "${action}" is attempting to override the "organizationId" or "organizationName" properties, which is not allowed when the 'includeCompany' argument is set to true.`
      );
      handleError(
        includeProperty && !!(additionalInfo.farmId ?? additionalInfo.farmName),
        `The event "${action}" is attempting to override the "farmId" or "farmName" properties, which is not allowed when the 'includeProperty' argument is set to true.`
      );
      handleError(
        includeWorkspace && !!(additionalInfo.workspaceId ?? additionalInfo.workspaceName),
        `The event "${action}" is attempting to override the "workspaceId" or "workspaceName" properties, which is not allowed when the 'includeWorkspace' argument is set to true.`
      );

      const payload: AnalyticsInfo = {
        ...companyPayload,
        ...propertyPayload,
        ...workspacePayload,
        ...planPayload,
        ...additionalInfo
      };

      amplitudeTrack(action, payload);
    },
    [
      company,
      property,
      includeCompany,
      includeProperty,
      propertyPayload,
      companyPayload,
      workspacePayload,
      planPayload,
      includeWorkspace,
      workspace
    ]
  );

  const identify = useCallback((userId: string, traits: Record<string, unknown>) => {
    setAnalyticsUser(userId);

    const userProperties = Object.entries(traits).reduce<Record<string, string>>((accumulator, [key, value]) => {
      if (value === null || value === undefined) {
        return accumulator;
      }

      if (typeof value === 'number' || typeof value === 'boolean') {
        accumulator[key] = String(value);
        return accumulator;
      }

      accumulator[key] = value as string;
      return accumulator;
    }, {});

    setAnalyticsUserProperties(userProperties);
  }, []);

  const reset = useCallback(() => {
    clearAnalyticsUser();
  }, []);

  return useMemo(
    () => ({
      track,
      identify,
      reset
    }),
    [track, identify, reset]
  );
};

export default useSegmentTracking;
